import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSidebarContext } from "../contexts/SidebarContext";
import { DashboardSidebar } from "../components/DashboardSidebar";
import { supabase } from "../lib/supabase";
import { toast } from "react-hot-toast";
import type { Database } from "../types/supabase";
import { NewsFormButtons } from "../components/NewsFormButtons";
import { ImageConfigModal } from "../components/ImageConfigModal";

type News = Database["public"]["Tables"]["news"]["Row"];
type NewsInput = Database["public"]["Tables"]["news"]["Insert"];

export interface NewsFormData {
  title: string;
  content: string;
  image_url?: string;
  external_link?: string;
  image_fit?: "cover" | "contain";
  status: "published" | "draft";
}

export function NewsForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { isCollapsed } = useSidebarContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<NewsFormData>({
    title: "",
    content: "",
    image_url: "",
    external_link: "",
    image_fit: "cover",
    status: "draft",
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [tempImagePreview, setTempImagePreview] = useState<string | null>(null);
  const [tempImageFile, setTempImageFile] = useState<File | null>(null);

  const isEditing = !!id;

  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  useEffect(() => {
    if (isEditing) {
      fetchNews();
    }
  }, [id]);

  async function fetchNews() {
    try {
      const { data, error } = await ((supabase
        .from("news") as any)
        .select("*")
        .eq("id", id)
        .single());

      if (error) throw error;
      if (data) {
        setFormData({
          title: data.title,
          content: data.content,
          image_url: data.image_url || "",
          external_link: data.external_link || "",
          image_fit: (data.image_fit as "cover" | "contain") || "cover",
          status: data.status,
        });
        if (data.image_url) {
          setImagePreview(data.image_url);
        }
      }
    } catch (error) {
      console.error("Error fetching news:", error);
      toast.error("Erro ao carregar a notícia");
    }
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      handleImageFile(file);
    } else {
      toast.error("Por favor, envie apenas arquivos de imagem");
    }
  }

  function handleImageFile(file: File) {
    setTempImageFile(file);
    setTempImagePreview(URL.createObjectURL(file));
    setShowImageModal(true);
  }

  async function handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      handleImageFile(file);
    }
  }

  function handleConfirmImage(imageFit: "cover" | "contain") {
    if (tempImagePreview) {
      if (tempImageFile) {
        setImageFile(tempImageFile);
      }
      setImagePreview(tempImagePreview);
      setFormData({ ...formData, image_fit: imageFit });
      setShowImageModal(false);
    }
  }

  function handleCancelImage() {
    setTempImageFile(null);
    setTempImagePreview(null);
    setShowImageModal(false);
  }

  function handleEditImage() {
    if (imagePreview) {
      setTempImagePreview(imagePreview);
      setTempImageFile(imageFile); 
      setShowImageModal(true);
    }
  }

  async function handleSubmit(e: React.FormEvent, statusOverride?: "published" | "draft") {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Usar statusOverride se fornecido, caso contrário usar o do formData
      const finalStatus = statusOverride || formData.status;
      
      let image_url = formData.image_url;

      if (imageFile) {
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from("images")
          .upload(`${Date.now()}-${imageFile.name}`, imageFile);

        if (uploadError) throw uploadError;

        const {
          data: { publicUrl },
        } = supabase.storage.from("images").getPublicUrl(uploadData.path);

        image_url = publicUrl;
      }

      const newsData = {
        title: formData.title,
        content: formData.content,
        image_url: image_url || null,
        external_link: formData.external_link || null,
        image_fit: formData.image_fit || "cover",
        status: finalStatus,
        updated_at: new Date().toISOString()
      };

      console.log("Enviando image_fit:", formData.image_fit); // Debug

      let result;
      if (isEditing) {
        result = await ((supabase
          .from("news") as any)
          .update(newsData)
          .eq("id", id)
          .select("*")
          .single());
      } else {
        result = await ((supabase
          .from("news") as any)
          .insert([newsData])
          .select("*")
          .single());
      }

      if (result.error) {
        console.error('Erro do Supabase:', result.error);
        throw result.error;
      }

      console.log('Resposta do Supabase:', result.data);
      console.log('image_fit salvo:', result.data?.image_fit);

      toast.success(
        isEditing
          ? `Notícia ${formData.status === "published" ? "publicada" : "salva como rascunho"} com sucesso!`
          : `Notícia ${formData.status === "published" ? "publicada" : "salva como rascunho"}!`
      );
      navigate("/dashboard/noticias");
    } catch (error: any) {
      console.error("Error saving news:", error);
      setError("Erro ao salvar a notícia. Por favor, tente novamente.");
      toast.error("Erro ao salvar a notícia");
    } finally {
      setLoading(false);
    }
  }

  if (!user) {
    return null;
  }

  return (
    <div className="flex min-h-screen bg-gray-100 overflow-x-hidden">
      <DashboardSidebar />
      <main 
        className={`flex-1 p-4 md:p-8 pb-24 w-full md:w-auto transition-all duration-300 min-w-0 ${
          isCollapsed ? "md:ml-20" : "md:ml-64"
        }`}
        style={{
          ...(window.innerWidth >= 768 && {
            width: isCollapsed ? 'calc(100vw - 5rem)' : 'calc(100vw - 16rem)'
          })
        }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="bg-white md:bg-transparent fixed md:relative top-0 left-0 right-0 md:top-auto md:left-auto md:right-auto z-10 px-4 py-3 md:p-0 shadow-sm md:shadow-none mb-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate("/dashboard/noticias")}
                className="p-2 -ml-2 md:hidden text-gray-600 hover:text-gray-800 rounded-full hover:bg-gray-100"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="w-6 h-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
                  />
                </svg>
              </button>
              <h1 className="text-lg md:text-2xl font-bold text-gray-800">
                {isEditing ? "Editar Notícia" : "Nova Notícia"}
              </h1>
            </div>
          </div>

          <div className="mt-16 md:mt-0">
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-lg shadow-md"
            >
              {error && (
                <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
                  {error}
                </div>
              )}

              <div className="p-4 md:p-6 space-y-4 md:space-y-6">
                <div>
                  <label
                    htmlFor="title"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Título
                  </label>
                  <input
                    type="text"
                    id="title"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#10a3b4] focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="content"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Conteúdo
                  </label>
                  <textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) =>
                      setFormData({ ...formData, content: e.target.value })
                    }
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#10a3b4] focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="external_link"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Link Externo{" "}
                    <span className="text-gray-500 text-xs">(opcional)</span>
                  </label>
                  <div className="relative">
                    <input
                      type="url"
                      id="external_link"
                      value={formData.external_link}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          external_link: e.target.value,
                        })
                      }
                      placeholder="https://exemplo.com/materia-completa"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#10a3b4] focus:border-transparent"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg
                        className="h-5 w-5 text-gray-400"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"
                        />
                      </svg>
                    </div>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Adicione um link para a fonte original ou matéria completa,
                    se houver
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Imagem da Notícia
                  </label>
                  {imagePreview && (
                    <div className="mb-4">
                      <div className="relative group rounded-lg shadow-md overflow-hidden border-2 border-gray-200">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-full max-h-[300px] object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity rounded-lg flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditImage();
                            }}
                            className="opacity-0 group-hover:opacity-100 bg-[#10a3b4] text-white px-4 py-2 rounded-md hover:bg-[#0d8997] transform hover:scale-105 transition-all flex items-center gap-2"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={2}
                              stroke="currentColor"
                              className="w-4 h-4"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                              />
                            </svg>
                            Editar imagem
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setImagePreview(null);
                              setImageFile(null);
                              setFormData({ ...formData, image_url: "" });
                            }}
                            className="opacity-0 group-hover:opacity-100 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transform hover:scale-110 transition-all"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={2}
                              stroke="currentColor"
                              className="w-5 h-5"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <p className="mt-2 text-sm text-gray-500 text-center">
                        Passe o mouse sobre a imagem para editar ou remover
                      </p>
                    </div>
                  )}
                  <div
                    className={`p-4 md:p-8 bg-gray-50 rounded-lg border-2 border-dashed transition-colors cursor-pointer ${
                      isDragging
                        ? "border-[#10a3b4] bg-blue-50"
                        : "border-gray-300 hover:border-[#10a3b4] hover:bg-gray-100"
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() =>
                      document.getElementById("file-upload")?.click()
                    }
                  >
                    <div className="text-center">
                      {!imagePreview && (
                        <>
                          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-[#e6f7f9] mb-4">
                            <svg
                              className="h-8 w-8 text-[#10a3b4]"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                              />
                            </svg>
                          </div>
                          <div className="space-y-2">
                            <div className="text-lg font-medium text-gray-700">
                              Adicione uma imagem à sua notícia
                            </div>
                            <div className="flex flex-col md:flex-row items-center justify-center gap-1 text-sm text-gray-600">
                              <label
                                htmlFor="file-upload"
                                className="relative cursor-pointer rounded-md font-medium text-[#10a3b4] hover:text-[#0d8997]"
                              >
                                <span>Clique para selecionar</span>
                                <input
                                  id="file-upload"
                                  name="file-upload"
                                  type="file"
                                  accept="image/*"
                                  className="sr-only"
                                  onChange={handleImageChange}
                                />
                              </label>
                              <p className="text-center md:text-left">
                                ou arraste e solte aqui
                              </p>
                            </div>
                            <p className="text-xs text-gray-500">
                              Formatos aceitos: PNG ou JPG (máximo 2MB)
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <NewsFormButtons
                loading={loading}
                setFormData={setFormData}
                formData={formData}
                onSubmit={handleSubmit}
              />
            </form>
          </div>
        </div>
      </main>

      {/* Modal de configuração da imagem */}
      <ImageConfigModal
        open={showImageModal}
        onOpenChange={setShowImageModal}
        imagePreview={tempImagePreview}
        currentImageFit={formData.image_fit || "cover"}
        onConfirm={handleConfirmImage}
        onCancel={handleCancelImage}
      />
    </div>
  );
}
